/**
 * Select Component - Enhanced dropdown with search and custom options
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { colors, spacing, borderRadius, zIndex } from '../../styles/design-tokens';

const Select = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  error,
  helper,
  className = '',
  variant = 'flat',
  size = 'md',
  required = false,
  searchable = false,
  clearable = false,
  fullWidth = true,
  disabled = false,
  loading = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  // Find selected option
  const selectedOption = options.find(opt => opt.value === value);

  // Filter options based on search
  const filteredOptions = searchable && searchTerm
    ? options.filter(opt => 
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange?.(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(null);
    setSearchTerm('');
  };

  const variants = {
    flat: 'bg-transparent border-b-2 border-neutral-200 focus:border-brand px-0 py-3',
    box: 'bg-white border-2 border-neutral-200 focus:border-brand px-4 py-3 rounded-lg',
    outline: 'bg-transparent border-2 border-neutral-200 focus:border-brand px-4 py-3 rounded-lg',
    ghost: 'bg-neutral-50 border-2 border-transparent focus:border-brand px-4 py-3 rounded-lg',
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-5 py-4 text-lg',
  };

  const hasError = !!error;

  return (
    <div ref={containerRef} className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        {/* Trigger */}
        <button
          type="button"
          className={`
            w-full flex items-center justify-between
            font-medium text-ink
            transition-all duration-200
            focus:outline-none
            disabled:opacity-50 disabled:cursor-not-allowed
            ${hasError ? 'border-error focus:border-error' : ''}
            ${variants[variant] || variants.flat}
            ${sizes[size] || sizes.md}
            ${className}
          `}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          {...props}
        >
          <span className={selectedOption ? 'text-ink' : 'text-muted'}>
            {selectedOption?.label || placeholder}
          </span>
          
          <div className="flex items-center gap-1">
            {clearable && selectedOption && (
              <button
                onClick={handleClear}
                className="p-0.5 hover:bg-neutral-100 rounded-full"
                aria-label="Clear selection"
              >
                <X size={14} className="text-neutral-400" />
              </button>
            )}
            <ChevronDown size={18} className={`text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-surface border border-neutral-200 rounded-lg shadow-lg overflow-hidden animate-fade-in">
            {/* Search */}
            {searchable && (
              <div className="p-2 border-b border-neutral-200">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-neutral-200 rounded-md focus:outline-none focus:border-brand"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}

            {/* Options */}
            <div className="max-h-56 overflow-y-auto">
              {loading ? (
                <div className="px-4 py-3 text-sm text-muted text-center">Loading...</div>
              ) : filteredOptions.length === 0 ? (
                <div className="px-4 py-3 text-sm text-muted text-center">No options found</div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = option.value === value;
                  return (
                    <button
                      key={option.value}
                      className={`
                        w-full px-4 py-2.5 text-left text-sm
                        transition-colors hover:bg-neutral-50
                        ${isSelected ? 'bg-brand-soft text-brand font-semibold' : 'text-ink'}
                        ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                      onClick={() => !option.disabled && handleSelect(option)}
                      disabled={option.disabled}
                    >
                      <div className="flex items-center gap-2">
                        {option.icon && (
                          <span className="text-neutral-400">{option.icon}</span>
                        )}
                        {option.label}
                        {isSelected && (
                          <span className="ml-auto text-brand">✓</span>
                        )}
                      </div>
                      {option.description && (
                        <p className="text-xs text-muted mt-0.5">{option.description}</p>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {helper && !hasError && (
        <p className="mt-1 text-xs text-muted">{helper}</p>
      )}
      
      {hasError && (
        <p className="mt-1 text-xs text-error">{error}</p>
      )}
    </div>
  );
};

Select.displayName = 'Select';

export default Select;